import React from 'react';

import { useViewType, ViewType } from '@eika/hooks';

import { useConfig } from '../../../defaultConfig';
import { paymentAgreementFromSelector, useAccountFreeWithdrawals, useAccounts } from '../../../fetchHooks/useAccount';
import { usePaymentAccountContext } from '../../../hooks/useAccountState';
import { useFormContext } from '../../../hooks/useForm';
import { useSigningContext } from '../../../signingContext/signingContext';
import filterInvalidAccounts from '../../../utils/filterInvalidAccounts';
import { RecurringPaymentMobileFields, RecurringPaymentDesktopFields, FieldProps } from './fields';
import { RecurringPaymentAction } from './form/actions';
import { RecurringPaymentState } from './form/state';
import { RecurringPaymentError } from './form/validate';

interface Props {
    locked?: boolean;
    shouldFocusFirstElement?: boolean;
}

const RecurringPaymentFields: React.FC<Props> = ({ locked = false, shouldFocusFirstElement = false }) => {
    const { featureToggles } = useConfig();

    const { data: validAccounts, ...accountState } = useAccounts({ select: paymentAgreementFromSelector });
    const { data: allAccounts } = useAccounts();
    const invalidAccounts = filterInvalidAccounts({ allAccounts, validAccounts });

    const form = useFormContext<RecurringPaymentState, RecurringPaymentAction, RecurringPaymentError>();
    const { state } = form;
    const { signingOperationPending } = useSigningContext();

    const { targetFromAccount } = usePaymentAccountContext();
    const { data: freeWithdrawals, showFreewithdrawal } = useAccountFreeWithdrawals(
        targetFromAccount,
        featureToggles.enableGettingAccountWithdrawalsInPayment,
    );

    const waitingForReceiver = locked && !state.toAccount;

    const viewType = useViewType();
    const isMobile = viewType === ViewType.mobile;

    const fieldProps: FieldProps = {
        form,
        signingOperationPending,
        locked,
        shouldFocusFirstElement,
        freeWithdrawals: { show: showFreewithdrawal, data: freeWithdrawals },
        receiverState: { isLoading: waitingForReceiver },
        date: { minDate: state.firstBankingDate, interval: state.paymentInterval },
        validAccounts,
        invalidAccounts,
        accountState,
    };
    return isMobile ? (
        <RecurringPaymentMobileFields {...fieldProps} />
    ) : (
        <RecurringPaymentDesktopFields {...fieldProps} />
    );
};

export default RecurringPaymentFields;
